package org.matthewo.api

import kotlin.jvm.JvmName
import kotlinx.serialization.Serializable
import kotlin.math.roundToInt
import org.matthewo.symbolprocessor.Period.*
import org.matthewo.schema.MetricsMeta
import org.matthewo.schema.games.*
import org.matthewo.math.plus
import org.matthewo.math.convert

@Serializable
data class MetricsIntoTheDeepAPI<N : Number>(
    override val total: N,
    override val npTotal: N,
    override val auto: N,
    override val teleop: N,
    override val endgame: N,
    override val penalty: N,
    override val apSampleNet: N,
    override val apSampleLow: N,
    override val apSampleHigh: N,
    override val apSpecimenLow: N,
    override val apSpecimenHigh: N,
    override val apNavigation: N,
    override val tpSampleNet: N,
    override val tpSampleLow: N,
    override val tpSampleHigh: N,
    override val tpSpecimenLow: N,
    override val tpSpecimenHigh: N,
    override val epNavigation: N,
) : MetricsAPI<N>, MetricsIntoTheDeep<N> {
    constructor(team1: MetricsIntoTheDeep<N>, team2: MetricsIntoTheDeep<N>) : this(
        total = team1.total + team2.total,
        npTotal = team1.npTotal + team2.npTotal,
        auto = team1.auto + team2.auto,
        teleop = team1.teleop + team2.teleop,
        endgame = team1.endgame + team2.endgame,
        penalty = team1.penalty + team2.penalty,
        apSampleNet = team1.apSampleNet + team2.apSampleNet,
        apSampleLow = team1.apSampleLow + team2.apSampleLow,
        apSampleHigh = team1.apSampleHigh + team2.apSampleHigh,
        apSpecimenLow = team1.apSpecimenLow + team2.apSpecimenLow,
        apSpecimenHigh = team1.apSpecimenHigh + team2.apSpecimenHigh,
        apNavigation = team1.apNavigation + team2.apNavigation,
        tpSampleNet = team1.tpSampleNet + team2.tpSampleNet,
        tpSampleLow = team1.tpSampleLow + team2.tpSampleLow,
        tpSampleHigh = team1.tpSampleHigh + team2.tpSampleHigh,
        tpSpecimenLow = team1.tpSpecimenLow + team2.tpSpecimenLow,
        tpSpecimenHigh = team1.tpSpecimenHigh + team2.tpSpecimenHigh,
        epNavigation = team1.epNavigation + team2.epNavigation,
    )
    
    companion object {
        inline operator fun <reified N : Number> invoke(team1: MetricsIntoTheDeep<*>, team2: MetricsIntoTheDeep<*>) = MetricsIntoTheDeepAPI(
            total = team1.total.convert<N>() + team2.total.convert<N>(),
            npTotal = team1.npTotal.convert<N>() + team2.npTotal.convert<N>(),
            auto = team1.auto.convert<N>() + team2.auto.convert<N>(),
            teleop = team1.teleop.convert<N>() + team2.teleop.convert<N>(),
            endgame = team1.endgame.convert<N>() + team2.endgame.convert<N>(),
            penalty = team1.penalty.convert<N>() + team2.penalty.convert<N>(),
            apSampleNet = team1.apSampleNet.convert<N>() + team2.apSampleNet.convert<N>(),
            apSampleLow = team1.apSampleLow.convert<N>() + team2.apSampleLow.convert<N>(),
            apSampleHigh = team1.apSampleHigh.convert<N>() + team2.apSampleHigh.convert<N>(),
            apSpecimenLow = team1.apSpecimenLow.convert<N>() + team2.apSpecimenLow.convert<N>(),
            apSpecimenHigh = team1.apSpecimenHigh.convert<N>() + team2.apSpecimenHigh.convert<N>(),
            apNavigation = team1.apNavigation.convert<N>() + team2.apNavigation.convert<N>(),
            tpSampleNet = team1.tpSampleNet.convert<N>() + team2.tpSampleNet.convert<N>(),
            tpSampleLow = team1.tpSampleLow.convert<N>() + team2.tpSampleLow.convert<N>(),
            tpSampleHigh = team1.tpSampleHigh.convert<N>() + team2.tpSampleHigh.convert<N>(),
            tpSpecimenLow = team1.tpSpecimenLow.convert<N>() + team2.tpSpecimenLow.convert<N>(),
            tpSpecimenHigh = team1.tpSpecimenHigh.convert<N>() + team2.tpSpecimenHigh.convert<N>(),
            epNavigation = team1.epNavigation.convert<N>() + team2.epNavigation.convert<N>(),
        )
    }
}

inline fun <reified N : Number> MetricsIntoTheDeep<*>.convert() = serialize { convert<N>() }

fun <N : Number> MetricsIntoTheDeep<N>.serialize() = MetricsIntoTheDeepAPI(
    total = total,
    npTotal = npTotal,
    auto = auto,
    teleop = teleop,
    endgame = endgame,
    penalty = penalty,
    apSampleNet = apSampleNet,
    apSampleLow = apSampleLow,
    apSampleHigh = apSampleHigh,
    apSpecimenLow = apSpecimenLow,
    apSpecimenHigh = apSpecimenHigh,
    apNavigation = apNavigation,
    tpSampleNet = tpSampleNet,
    tpSampleLow = tpSampleLow,
    tpSampleHigh = tpSampleHigh,
    tpSpecimenLow = tpSpecimenLow,
    tpSpecimenHigh = tpSpecimenHigh,
    epNavigation = epNavigation,
)

@JvmName("serializeGeneric")
fun <T : Number, R : Number> MetricsIntoTheDeep<T>.serialize(serializer: T.() -> R) = MetricsIntoTheDeepAPI(
    total = total.serializer(),
    npTotal = npTotal.serializer(),
    auto = auto.serializer(),
    teleop = teleop.serializer(),
    endgame = endgame.serializer(),
    penalty = penalty.serializer(),
    apSampleNet = apSampleNet.serializer(),
    apSampleLow = apSampleLow.serializer(),
    apSampleHigh = apSampleHigh.serializer(),
    apSpecimenLow = apSpecimenLow.serializer(),
    apSpecimenHigh = apSpecimenHigh.serializer(),
    apNavigation = apNavigation.serializer(),
    tpSampleNet = tpSampleNet.serializer(),
    tpSampleLow = tpSampleLow.serializer(),
    tpSampleHigh = tpSampleHigh.serializer(),
    tpSpecimenLow = tpSpecimenLow.serializer(),
    tpSpecimenHigh = tpSpecimenHigh.serializer(),
    epNavigation = epNavigation.serializer(),
)

@JvmName("serializeDouble")
fun MetricsIntoTheDeep<Double>.serialize() = serialize { roundToInt().toShort() }

@JvmName("serializeNumber")
fun MetricsIntoTheDeep<Number>.serialize() = serialize { toShort() }

fun MetricsIntoTheDeep<*>.stringify() = """
        total: $total
        npTotal: $npTotal
        auto: $auto
        teleop: $teleop
        endgame: $endgame
        penalty: $penalty
        apSampleNet: $apSampleNet
        apSampleLow: $apSampleLow
        apSampleHigh: $apSampleHigh
        apSpecimenLow: $apSpecimenLow
        apSpecimenHigh: $apSpecimenHigh
        apNavigation: $apNavigation
        tpSampleNet: $tpSampleNet
        tpSampleLow: $tpSampleLow
        tpSampleHigh: $tpSampleHigh
        tpSpecimenLow: $tpSpecimenLow
        tpSpecimenHigh: $tpSpecimenHigh
        epNavigation: $epNavigation
    """.trimIndent()

object MetricsIntoTheDeepMeta : MetricsMeta<MetricsIntoTheDeep<*>> {
    override val names = linkedMapOf(
        MetricsIntoTheDeep<*>::total to "Total",
        MetricsIntoTheDeep<*>::npTotal to "NP Total",
        MetricsIntoTheDeep<*>::auto to "Auto",
        MetricsIntoTheDeep<*>::teleop to "Teleop",
        MetricsIntoTheDeep<*>::endgame to "Endgame",
        MetricsIntoTheDeep<*>::penalty to "Foul",
        MetricsIntoTheDeep<*>::apSampleNet to "Sample Net",
        MetricsIntoTheDeep<*>::apSampleLow to "Sample Low",
        MetricsIntoTheDeep<*>::apSampleHigh to "Sample High",
        MetricsIntoTheDeep<*>::apSpecimenLow to "Specimen Low",
        MetricsIntoTheDeep<*>::apSpecimenHigh to "Specimen High",
        MetricsIntoTheDeep<*>::apNavigation to "Navigation",
        MetricsIntoTheDeep<*>::tpSampleNet to "Sample Net",
        MetricsIntoTheDeep<*>::tpSampleLow to "Sample Low",
        MetricsIntoTheDeep<*>::tpSampleHigh to "Sample High",
        MetricsIntoTheDeep<*>::tpSpecimenLow to "Specimen Low",
        MetricsIntoTheDeep<*>::tpSpecimenHigh to "Specimen High",
        MetricsIntoTheDeep<*>::epNavigation to "Navigation",
    )
    
    override val periods = linkedMapOf(
        MetricsIntoTheDeep<*>::total to ALL,
        MetricsIntoTheDeep<*>::npTotal to ALL,
        MetricsIntoTheDeep<*>::auto to AUTO,
        MetricsIntoTheDeep<*>::teleop to TELEOP,
        MetricsIntoTheDeep<*>::endgame to ENDGAME,
        MetricsIntoTheDeep<*>::penalty to ALL,
        MetricsIntoTheDeep<*>::apSampleNet to AUTO,
        MetricsIntoTheDeep<*>::apSampleLow to AUTO,
        MetricsIntoTheDeep<*>::apSampleHigh to AUTO,
        MetricsIntoTheDeep<*>::apSpecimenLow to AUTO,
        MetricsIntoTheDeep<*>::apSpecimenHigh to AUTO,
        MetricsIntoTheDeep<*>::apNavigation to AUTO,
        MetricsIntoTheDeep<*>::tpSampleNet to TELEOP,
        MetricsIntoTheDeep<*>::tpSampleLow to TELEOP,
        MetricsIntoTheDeep<*>::tpSampleHigh to TELEOP,
        MetricsIntoTheDeep<*>::tpSpecimenLow to TELEOP,
        MetricsIntoTheDeep<*>::tpSpecimenHigh to TELEOP,
        MetricsIntoTheDeep<*>::epNavigation to ENDGAME,
    )
}
